import * as React from 'react'
import { Download } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'

interface ChartExportButtonProps {
  /** Ref to the container div wrapping the ResponsiveContainer/chart */
  containerRef: React.RefObject<HTMLDivElement | null>
  /** Filename without extension */
  fileName?: string
}

function getSvgElement(container: HTMLDivElement): SVGSVGElement | null {
  return container.querySelector('svg.recharts-surface') ?? container.querySelector('svg')
}

function serializeSvg(svgEl: SVGSVGElement): string {
  const clone = svgEl.cloneNode(true) as SVGSVGElement
  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg')
  if (!clone.getAttribute('width')) {
    const rect = svgEl.getBoundingClientRect()
    clone.setAttribute('width', String(Math.round(rect.width)))
    clone.setAttribute('height', String(Math.round(rect.height)))
  }
  // Add dark background for standalone viewing
  const bg = document.createElementNS('http://www.w3.org/2000/svg', 'rect')
  bg.setAttribute('width', '100%')
  bg.setAttribute('height', '100%')
  bg.setAttribute('fill', '#09090b')
  clone.insertBefore(bg, clone.firstChild)
  return new XMLSerializer().serializeToString(clone)
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

function handleSvgExport(container: HTMLDivElement, fileName: string) {
  const svgEl = getSvgElement(container)
  if (!svgEl) return
  const svgString = serializeSvg(svgEl)
  const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' })
  downloadBlob(blob, `${fileName}.svg`)
}

function handlePngExport(container: HTMLDivElement, fileName: string, scale: number = 2) {
  const svgEl = getSvgElement(container)
  if (!svgEl) return
  const svgString = serializeSvg(svgEl)
  const rect = svgEl.getBoundingClientRect()
  const width = Math.round(rect.width) * scale
  const height = Math.round(rect.height) * scale

  const img = new Image()
  const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' })
  const url = URL.createObjectURL(svgBlob)

  img.onload = () => {
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')
    if (!ctx) { URL.revokeObjectURL(url); return }
    ctx.drawImage(img, 0, 0, width, height)
    canvas.toBlob((blob) => {
      if (blob) downloadBlob(blob, `${fileName}.png`)
      URL.revokeObjectURL(url)
    }, 'image/png')
  }
  img.onerror = () => URL.revokeObjectURL(url)
  img.src = url
}

export function ChartExportButton({ containerRef, fileName = 'chart' }: ChartExportButtonProps) {
  const [open, setOpen] = React.useState(false)

  return (
    <div className="relative inline-flex">
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => setOpen(!open)}
              aria-label="Export chart"
            >
              <Download className="h-3.5 w-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            <p>Export chart</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      {open && (
        <div className="absolute right-0 top-8 z-50 rounded-md border bg-popover p-1 shadow-md text-popover-foreground animate-in fade-in-0 zoom-in-95">
          <button
            className="flex w-full items-center gap-2 rounded-sm px-3 py-1.5 text-xs hover:bg-accent hover:text-accent-foreground"
            onClick={() => { if (containerRef.current) handleSvgExport(containerRef.current, fileName); setOpen(false) }}
          >
            SVG (vector)
          </button>
          <button
            className="flex w-full items-center gap-2 rounded-sm px-3 py-1.5 text-xs hover:bg-accent hover:text-accent-foreground"
            onClick={() => { if (containerRef.current) handlePngExport(containerRef.current, fileName, 2); setOpen(false) }}
          >
            PNG (2x)
          </button>
          <button
            className="flex w-full items-center gap-2 rounded-sm px-3 py-1.5 text-xs hover:bg-accent hover:text-accent-foreground"
            onClick={() => { if (containerRef.current) handlePngExport(containerRef.current, fileName, 4); setOpen(false) }}
          >
            PNG (4x, publication)
          </button>
        </div>
      )}
    </div>
  )
}
